import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Users, Truck, Loader2, Merge, ChevronRight } from "lucide-react";

export default function PortfolioVendorsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMerge, setShowMerge] = useState(false);
  const [mergeOwner, setMergeOwner] = useState("");
  const [mergeTrucks, setMergeTrucks] = useState("");
  const [merging, setMerging] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/portfolio-vendors`).then(res => {
      setData(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleMerge = async () => {
    setMerging(true);
    try {
      const ids = mergeTrucks.split(",").map(s => s.trim()).filter(Boolean);
      await axios.post(`${API}/admin/portfolio-merge`, {
        owner_user_id: mergeOwner.trim(),
        truck_business_ids: ids,
        portfolio_name: "",
      });
      setShowMerge(false);
      setMergeOwner("");
      setMergeTrucks("");
      // Refresh
      const res = await axios.get(`${API}/admin/portfolio-vendors`);
      setData(res.data);
    } catch {}
    setMerging(false);
  };

  if (loading) return <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#F59E0B]" /></div>;

  return (
    <div data-testid="admin-portfolio-vendors">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-bold text-white">{data?.count || 0} Portfolio Vendors</span>
        </div>
        <button onClick={() => setShowMerge(!showMerge)} className="text-[10px] text-amber-500 font-medium flex items-center gap-1" data-testid="admin-merge-btn">
          <Merge className="w-3 h-3" /> Merge Accounts
        </button>
      </div>

      {showMerge && (
        <div className="bg-[#1C1C1E] rounded-xl p-3 mb-3 border border-white/10" data-testid="admin-merge-form">
          <p className="text-xs text-white/50 mb-2">Merge vendor accounts into a portfolio</p>
          <input
            value={mergeOwner}
            onChange={e => setMergeOwner(e.target.value)}
            placeholder="Owner User ID"
            className="w-full px-2 py-1.5 rounded-lg bg-[#2C2C2E] text-white text-xs mb-2 outline-none border border-white/10"
            data-testid="merge-owner-input"
          />
          <input
            value={mergeTrucks}
            onChange={e => setMergeTrucks(e.target.value)}
            placeholder="Truck IDs (comma separated)"
            className="w-full px-2 py-1.5 rounded-lg bg-[#2C2C2E] text-white text-xs mb-2 outline-none border border-white/10"
            data-testid="merge-trucks-input"
          />
          <button onClick={handleMerge} disabled={merging} className="w-full py-2 rounded-lg bg-amber-500 text-black text-xs font-bold disabled:opacity-50" data-testid="merge-submit-btn">
            {merging ? "Merging..." : "Merge"}
          </button>
        </div>
      )}

      {(!data?.portfolios?.length) ? (
        <p className="text-xs text-white/30 text-center py-4">No portfolio vendors yet</p>
      ) : (
        <div className="space-y-2">
          {data.portfolios.map(p => (
            <div key={p.portfolio_id} className="bg-[#1C1C1E] rounded-xl border border-white/5">
              <button
                onClick={() => setExpanded(expanded === p.portfolio_id ? null : p.portfolio_id)}
                className="w-full flex items-center gap-3 p-3"
                data-testid={`portfolio-row-${p.portfolio_id}`}
              >
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Truck className="w-4 h-4 text-purple-500" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white">{p.portfolio_name || p.owner?.name || "Portfolio"}</p>
                  <p className="text-[10px] text-white/40">{p.truck_count} trucks | {p.owner?.email}</p>
                </div>
                <ChevronRight className={`w-4 h-4 text-white/30 transition ${expanded === p.portfolio_id ? "rotate-90" : ""}`} />
              </button>
              {expanded === p.portfolio_id && (
                <div className="px-3 pb-3 space-y-1.5">
                  {p.trucks.map(t => (
                    <div key={t.business_id} className="flex items-center gap-2 px-2 py-1.5 bg-[#2C2C2E]/50 rounded-lg">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color_assignments?.[t.business_id] || "#F59E0B" }} />
                      <p className="text-xs text-white/70">{t.name}</p>
                      <p className="text-[9px] text-white/30 ml-auto">{t.business_id}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
